import moment from "moment-timezone";
import { confirmGuestPhoneNumberOtp, createGuestUserAccount } from "../../../redux/actions/authActions";
import { setAssesmentData } from "../../../redux/actions/commonActions";
import { store } from "../../../redux/store";
import {
  createBooking,
  createWaitlistBooking,
  fetchAvailabilitiesByScheduleId,
  fetchOrganizationById,
  fetchOrganizations,
  fetchProductsByOrgId,
  fetchSchedulesByServiceId,
  fetchServicesbyOrgId,
  prepareWaitlistBooking,
} from "../../../redux/actions/apiActions";
import axios from "axios";

const DEFAULT_TIMEZONE = "Asia/Kolkata";

const fmtTime = (sec, timezoneId = DEFAULT_TIMEZONE) => {
  if (!sec) return "";
  return moment.unix(sec).tz(timezoneId).format("ddd, MMM D, hh:mm A");
};

const fmtDateLabel = (date, timezoneId = DEFAULT_TIMEZONE) => {
  if (!date) return "";
  return moment.tz(date, "YYYY-MM-DD", timezoneId).format("ddd, MMM D");
};

const fmtPrice = (amount, currency) => {
  if (amount == null) return "";
  if (amount === 0) return "Free";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

export default class QueZoneBookingRepository {
  constructor({ dispatch, orgRef }) {
    this.dispatch = dispatch;
    this.orgRef = orgRef;
    this.org = null;
    this.orgList = [];
    this.services = [];
    this.products = [];
    this.schedulesByServiceId = {};
    this.slotsByScheduleId = {};
  }

  async findOrganizations(searchText) {
    if (!this.orgList.length) {
      this.orgList = await this.dispatch(fetchOrganizations());
    }
    const search = (searchText || "").trim().toLowerCase();
    if (!search) {
      return [];
    }
    return this.orgList.filter(org =>
      (org.name || "").toLowerCase().includes(search)
    );
  }

  async getOrganization(orgId = null) {
    if (!this.org || (orgId && String(this.org.id) !== String(orgId))) {
      this.services = [];
      this.products = [];
      this.schedulesByServiceId = {};
      this.slotsByScheduleId = {};
      this.org = await this.dispatch(fetchOrganizationById(orgId || this.orgRef));
    }
    return this.org;
  }

  async listServices(productId = null) {
    if (!this.services.length) {
      await this.getOrganization();
      let services = await this.dispatch(fetchServicesbyOrgId(this.org?.orgRef || this.orgRef)) || [];
      this.services = services.filter(i => !i?.inactive && !i?.hidden);
    }

    if (productId) {
      const products = await this.listProducts();
      const product = products.find((item) => String(item.id) === String(productId));

      if (!product) throw new Error("Selected product was not found.");

      const productServices = new Map((product.services || []).map((item) => [String(item.serviceId), item]));
      const serviceIds = new Set(productServices.keys());

      return this.services
        .filter((service) => serviceIds.has(String(service.id)))
        .map((service) => {
          const productService = productServices.get(String(service.id));
          if (!productService) return service;

          const { serviceId, ...overrideFields } = productService;
          return {
            ...service,
            ...overrideFields,
          };
        });
    }

    return this.services;
  }
  async listProducts() {
    if (!this.products.length) {
      await this.getOrganization();
      const products = await this.dispatch(fetchProductsByOrgId(this.org?.id || this.orgRef)) || [];
      this.products = products.filter(i => !i?.hidden);
    }
    return this.products;
  }

  async listSchedules(serviceId, productId = null) {
    if (!serviceId) throw new Error("serviceId is required.");

    let productServicePrice = null;
    if (productId) {
      const products = await this.listProducts();
      const product = products.find((item) => String(item.id) === String(productId));
      if (product) {
        const productService = (product.services || []).find((item) => String(item.serviceId) === String(serviceId));
        if (productService?.price) {
          productServicePrice = productService.price;
        }
      }
    }

    if (!this.schedulesByServiceId[serviceId]) {
      this.schedulesByServiceId[serviceId] = await this.dispatch(fetchSchedulesByServiceId(serviceId)) || [];
    }

    if (!productServicePrice) {
      return this.schedulesByServiceId[serviceId];
    }

    return this.schedulesByServiceId[serviceId].map((schedule) => ({
      ...schedule,
      payment: {
        ...schedule.payment,
        amount: productServicePrice.value ?? schedule.payment?.amount,
        currency: productServicePrice.currency ?? schedule.payment?.currency,
        enablePayment: productServicePrice.enablePayment ?? schedule.payment?.enablePayment,
      },
    }));
  }

  async listAvailableSlots(scheduleId) {
    if (!scheduleId) throw new Error("scheduleId is required.");

    if (!this.slotsByScheduleId[scheduleId]) {
      this.slotsByScheduleId[scheduleId] = await this.dispatch(fetchAvailabilitiesByScheduleId(scheduleId)) || [];
    }

    return this.slotsByScheduleId[scheduleId];
  }

  async listAvailableDates(scheduleId) {
    const slots = await this.listAvailableSlots(scheduleId);
    const timezoneId = this.getScheduleTimezone(scheduleId);
    const datesByValue = slots.reduce((acc, slot) => {
      if (!slot?.startSec) return acc;

      const date = moment.unix(slot.startSec).tz(timezoneId).format("YYYY-MM-DD");
      const current = acc[date] || {
        date,
        label: fmtDateLabel(date, timezoneId),
        timezoneId,
        slotCount: 0,
        firstStartSec: slot.startSec,
      };

      current.slotCount += 1;
      current.firstStartSec = Math.min(current.firstStartSec, slot.startSec);
      acc[date] = current;
      return acc;
    }, {});

    return Object.values(datesByValue)
      .sort((first, second) => first.firstStartSec - second.firstStartSec)
      .map(({ firstStartSec, ...dateDetails }) => dateDetails);
  }

  async listAvailableSlotsByDate(scheduleId, date) {
    if (!date) throw new Error("date is required in YYYY-MM-DD format.");
    if (!moment(date, "YYYY-MM-DD", true).isValid()) {
      throw new Error("date must be in YYYY-MM-DD format.");
    }

    const timezoneId = this.getScheduleTimezone(scheduleId);
    const slots = await this.listAvailableSlots(scheduleId);

    return slots
      .filter((slot) => slot?.startSec && moment.unix(slot.startSec).tz(timezoneId).format("YYYY-MM-DD") === date)
      .sort((first, second) => first.startSec - second.startSec);
  }

  getScheduleTimezone(scheduleId) {
    const schedules = Object.values(this.schedulesByServiceId).flat();
    const schedule = schedules.find((item) => String(item.id) === String(scheduleId));
    return schedule?.timezoneId || DEFAULT_TIMEZONE;
  }

  async verifyBookingOTP(mobileNumber, otp) {
    const otpVerificationResponse = await this.dispatch(confirmGuestPhoneNumberOtp(mobileNumber, otp)) || "Failed to verify OTP. Please try again.";
    return otpVerificationResponse;
  }

  async bookAppointment({ customerName, customerPhone, customerEmail = "", serviceId, scheduleId, availabilityId, startSec, productId = null, additionalInfoFormData=null }) {
    if (!customerName || !customerPhone || !serviceId || !scheduleId || !availabilityId || !startSec) {
      throw new Error("Customer, service, schedule, slot, and start time are required before booking.");
    }

    const [services, schedules] = await Promise.all([
      this.listServices(),
      this.listSchedules(serviceId),
    ]);
    const service = services.find((item) => String(item.id) === String(serviceId));
    const schedule = schedules.find((item) => String(item.id) === String(scheduleId));

    if (!service) throw new Error("Selected service was not found.");
    if (!schedule) throw new Error("Selected schedule was not found.");

    // If schedule has a survey and no additionalInfo passed, try to use assessment data from redux store
    if (schedule?.surveyId && !additionalInfoFormData) {
      const assessment = store.getState()?.common?.assessmentData;
      if (assessment && assessment?.surveyId && String(assessment.surveyId) === String(schedule.surveyId)) {
        additionalInfoFormData = assessment;
      } else {
        return {
          status: "ADDITIONAL_INFO_REQUIRED",
          surveyId: schedule.surveyId,
          message: "Additional Info required from the customer.",
        };
      }
    }

    // Reuse existing guest user from redux if phone/email matches to avoid duplicate users
    let userDetails = null;
    try {
      const existingUser = store.getState()?.auth?.userData || {};
      const existingPhone = existingUser?.telephone || existingUser?.phone || existingUser?.mobile;
      const existingEmail = existingUser?.email;
      if ((existingPhone && existingPhone === customerPhone) || (existingEmail && existingEmail === customerEmail)) {
        userDetails = existingUser;
      }
    } catch (e) {
      // ignore
    }

    if (!userDetails) {
      userDetails = await this.dispatch(createGuestUserAccount({
        email: customerEmail,
        givenName: customerName,
        phone: customerPhone,
        tmpServiceId: scheduleId,
        userType: "GUEST",
      }));

      if (!userDetails?.id) {
        throw new Error("Could not create or load guest customer.");
      }
    }

    if (userDetails.userType === "GUEST" && !userDetails.isPhoneVerified && userDetails.otpSend) {
      return {
        status: "OTP_REQUIRED",
        customerId: userDetails.id,
        message: "Phone verification is required before the booking can be finalized. OTP sent to the provided phone number. Please verify the OTP to complete the booking.",
      };
    }

    if(userDetails?.token){
      axios.defaults.headers.common['Authorization'] = `Bearer ${userDetails.token}`
    }
    
    let bookingDetails;
    if (service.mode === "QUEUE") {
      const waitListDetails = await this.dispatch(prepareWaitlistBooking({
        customerId: userDetails.id,
        startSec: moment(startSec * 1000).startOf("day").unix(),
        tempServiceId: scheduleId,
        toSec: moment(startSec * 1000).endOf("day").unix(),
      }));

      if (!waitListDetails?.availabilityId) {
        throw new Error("Could not prepare the queue booking.");
      }

      bookingDetails = await this.dispatch(createWaitlistBooking({
        customerId: waitListDetails.customerId || userDetails.id,
        productId: productId,
        duration: schedule.avgServiceTime,
        availabilityId: waitListDetails.availabilityId,
        startSec: waitListDetails.startSec,
        status: "UNSPECIFIED",
        type: "APPOINTMENT",
        responseSurveys: additionalInfoFormData ?? {},
      }));
    } else {
      bookingDetails = await this.dispatch(createBooking({
        customerId: userDetails.id,
        productId: productId,
        duration: schedule.avgServiceTime,
        availabilityId,
        startSec,
        status: "UNSPECIFIED",
        type: "APPOINTMENT",
        responseSurveys: additionalInfoFormData ?? {},
      }));
    }

    if (!bookingDetails?.id) {
      throw new Error("Booking could not be completed.");
    }

    // Clear any stored assessment data for this survey since booking succeeded
    try {
      await this.dispatch(setAssesmentData({}));
    } catch (e) {
      // ignore
    }

    return {
      status: "CONFIRMED",
      booking: bookingDetails,
    };
  }

  serializeOrganizations(orgs = []) {
    return orgs.map((org) => ({
      id: org.id,
      name: org.name,
      description: org.description,
      orgRef: org.orgRef,
      preferences: {
        enableProducts: Boolean(org.preferences?.enableProducts),
      },
    }));
  }

  serializeOrganization(org) {
    if (!org) return null;
    return {
      id: org.id,
      name: org.name,
      description: org.description,
      orgRef: org.orgRef,
      preferences: {
        enableProducts: Boolean(org.preferences?.enableProducts),
      },
    };
  }

  serializeProducts(products = []) {
    return products.map((product) => ({
      id: product.id,
      name: product.name,
      description: product.description,
    }));
  }

  serializeServices(services = []) {
    return services.map((service) => ({
      id: service.id,
      name: service.name,
      mode: service.mode,
      description: service.description,
      tags: service.tags,
      price: service.price,
    }));
  }

  serializeSchedules(schedules = []) {
    return schedules.map((schedule) => ({
      id: schedule.id,
      serviceId: schedule.serviceId,
      providerName: schedule.providerName,
      isOnline: schedule.isOnline,
      onlineMeetingProvider: schedule.onlineMeetingProvider,
      location: schedule.geoLocation?.name,
      price: fmtPrice(schedule.payment?.amount, schedule.payment?.currency),
      avgServiceTime: schedule.avgServiceTime,
      dateLabel: schedule.dateLabel,
      timezoneId: schedule.timezoneId,
    }));
  }

  serializeSlots(slots = [], scheduleId) {
    const timezoneId = this.getScheduleTimezone(scheduleId);
    return slots.map((slot) => ({
      id: slot.id,
      startSec: slot.startSec,
      time: fmtTime(slot.startSec, timezoneId),
    }));
  }
}
